import type { WidenLocale } from '@mangovoice/types'

export const pbxSettingsEn = {
  loading: 'Loading PBX settings...',
  invalidPbx: {
    title: 'Invalid PBX',
    description:
      'A valid PBX ID is required to edit PBX settings. Please select a PBX from the dashboard.',
  },
  loadError: {
    title: 'Unable to load PBX settings',
    description: 'Please try again.',
  },
  partialLoadWarning: {
    title: 'Some data failed to load',
    unknownError: 'Unknown error',
    labels: {
      providerGateways: 'Provider gateways',
      serverGroups: 'Server groups',
      phoneNumbers: 'Phone numbers',
      virtualFaxes: 'Virtual faxes',
      alert911Options: '911 options',
    },
  },
  actions: {
    update: 'Update',
  },
  common: {
    loading: 'Loading...',
    select: 'Select',
  },
  sections: {
    core: {
      title: 'PBX Settings',
    },
    advanced: {
      title: 'Advanced PBX Settings',
    },
    system: {
      title: 'System Settings',
    },
  },
  placeholders: {
    selectFromList: 'Select from the List...',
    selectServerGroup: 'Select server group',
    selectTimeZone: 'Select time zone',
    selectExtensionDigits: 'Select extension digits',
    selectCodec: 'Select codec',
  },
  fields: {
    pbxDisplayName: {
      label: 'PBX Display Name',
    },
    company: {
      label: 'Company',
    },
    providerGateway: {
      label: 'Provider gateway',
    },
    emergencyProviderGateway: {
      label: 'Emergency provider gateway',
    },
    internationalProviderGateway: {
      label: 'International provider gateway',
    },
    active: {
      label: 'Active',
    },
    smsSenderName: {
      label: 'SMS Sender Name',
      copyFromCompany: 'Copy from Company',
      helperText:
        'Shown to your customers in SMS opt-out replies, e.g. "{{name}}: You have successfully unsubscribed." This is per-location — edit it here if it differs from the company name.',
      divergenceHint:
        'Differs from company name "{{companyName}}" — this is expected for multi-location customers.',
    },
    serverGroup: {
      label: 'Server Group',
    },
    advanced: {
      maximum_allowed_channels: 'Max Channels',
      maximum_lines_in: 'Inbound Channels',
      maximum_lines_out: 'Per Extension Channels',
      call_log_retention_period_in_months: 'Call History (in months)',
    },
    industry: {
      label: 'Industry',
    },
    ehr: {
      label: 'EHR',
      helperText: 'For reporting purposes only, does not impact functionality',
    },
    defaultEmergencyAlert: {
      label: 'Default Emergency Alert',
    },
    defaultTimeZone: {
      label: 'Default Time Zone',
      britishColumbia: 'Pacific Time - British Columbia (PCT)',
    },
    callParkTimeout: {
      label: 'Call Park Timeout',
    },
    extensionDigits: {
      label: 'Extension Digits',
    },
    defaultAreaCode: {
      label: 'Default Area Code',
    },
    defaultCallerId: {
      label: 'Default Caller ID',
    },
    defaultFaxNumber: {
      label: 'Default Fax Number',
    },
    defaultVirtualFax: {
      label: 'Default Virtual Fax',
    },
    preferredCodec: {
      label: 'Preferred Codec',
    },
    checkboxes: {
      enable_inbound_recording: 'Inbound Call Recording',
      enable_outbound_recording: 'Outbound Call Recording',
      enable_internal_recording: 'Internal Call Recording',
      international: 'International',
      enable_vm_transcription: 'Voicemail Transcription',
      schedules_v2: 'Schedule V2',
      enable_agent_statuses: 'Call Center',
      enable_call_analytics: 'Mango AI',
      is_grove: 'Grove',
    },
  },
  companySelect: {
    placeholder: 'Select Company',
    ariaLabel: 'Company selector',
    searchPlaceholder: 'Search companies...',
    empty: {
      loading: 'Loading companies...',
      noResults: 'No companies found',
      typeToSearch: 'Type to search companies',
    },
  },
  phoneSelect: {
    placeholder: 'Select from the List...',
    ariaLabel: 'Phone number selector',
    searchPlaceholder: 'Search phone numbers...',
    empty: {
      loading: 'Loading phone numbers...',
      noResults: 'No phone numbers found',
      noData: 'No phone numbers available',
    },
  },
  toast: {
    validationError: 'Validation error',
    timezoneChanged: {
      title: 'Timezone changed',
      description:
        'You changed the PBX timezone. Please remember to update your schedules accordingly.',
    },
    noChanges: 'No changes to save',
    saveSuccess: 'PBX settings updated',
    saveError: {
      title: 'Update failed',
      description: 'There was an issue updating PBX settings.',
    },
  },
  callParkConfirm: {
    title: 'Confirm Call Park Timeout',
    description:
      'Are you sure you want to keep callers in the parking lot for longer than 10 minutes?',
    cancel: 'Reset timeout',
    confirm: 'Continue',
  },
  rebuildAndReboot: {
    title: 'PBX settings updated',
    description:
      'Some PBX settings may not take effect on devices until they have been rebuilt and rebooted.',
    cancel: 'Not now',
    confirm: 'Rebuild and Reboot Devices',
    toast: {
      successTitle: 'Rebuild and reboot started',
      successDescription: 'All devices are being rebuilt and rebooted.',
      errorTitle: 'Rebuild and reboot failed',
      errorDescription: 'There was an issue rebuilding and rebooting devices.',
    },
  },
  validation: {
    pbxDisplayNameRequired: 'Please make an entry for the PBX Display Name.',
    companyRequired: 'Please select a Company.',
    smsSenderNameRequired: 'Please make an entry for the SMS Sender Name.',
    serverGroupRequired: 'Please select a Server Group.',
    primaryOutboundGatewayRequired: 'Please select a Primary Outbound Gateway.',
    primaryInternationalGatewayRequired:
      'Please select a Primary International Gateway.',
    emergencyGatewayRequired: 'Please select an Emergency Gateway.',
    industryRequired: 'Please select an Industry.',
    maxChannelsRequired: 'Please make an entry for the Max Channels.',
    inboundChannelsRequired: 'Please make an entry for the Inbound Channels.',
    perExtensionChannelsRequired:
      'Please make an entry for the Per Extension Channels.',
    callHistoryRequired: 'Please make an entry for the Call History.',
    numericFieldRange: '{{field}} must be between {{min}} and {{max}}.',
    numericFieldMin: 'Minimum is {{min}}.',
    numericFieldMax: 'Maximum is {{max}}.',
    defaultTimeZoneRequired: 'Please select a Default Time Zone.',
    callParkTimeoutRange:
      'Please make an entry for Call Park Timeout between {{min}}-{{max}} (Recommended: {{recommended}}).',
    callParkTimeoutMax: 'Call Park Timeout cannot exceed {{max}} seconds.',
    extensionDigitsRequired: 'Please make an entry for the Extension Digits.',
    extensionDigitsInvalid: 'Extension Digits must be 3, 4, or 5.',
    defaultAreaCodeRequired: 'Please make an entry for the Default Area Code.',
    defaultAreaCodeDigits: 'Default Area Code must be a 3-digit number.',
    defaultAreaCodeRange: 'Default Area Code must be between 200 and 998.',
    defaultAreaCodeDisallowed:
      'The Area Code {{code}} is not permitted for the Default Area Code.',
    defaultCallerIdRequired: 'Please select a Default Caller ID.',
    defaultFaxNumberRequired: 'Please select a Default Fax Number.',
    defaultVirtualFaxRequired: 'Please select a Default Virtual Fax.',
    preferredCodecRequired: 'Please select a Preferred Codec.',
  },
} as const

export type PbxSettingsLocale = WidenLocale<typeof pbxSettingsEn>
